export interface SecurityHeaders {
  "Content-Security-Policy": string;
  "X-Content-Type-Options": string;
  "X-Frame-Options": string;
  "X-XSS-Protection": string;
  "Strict-Transport-Security": string;
  "Referrer-Policy": string;
  "Permissions-Policy": string;
}

/** NU Core SSO issuer (#590). The browser only navigates there today; listed
 *  so a future direct call (e.g. userinfo) is not silently blocked. */
export const SSO_ORIGIN = "https://sso.nugui.co";

/**
 * @param extraConnectSrc origins resolved at runtime (#590/#940): the Lane A
 *   RPC origin and the NU GUI gateway broker origin come from
 *   /runtime-config.json, so they cannot be hard-coded here.
 */
export const getSecurityHeaders = (
  isDevelopment: boolean = false,
  extraConnectSrc: readonly string[] = [],
): SecurityHeaders => {
  const extra = extraConnectSrc.filter((o) => /^https?:\/\/[^\s;'"]+$/.test(o));
  const baseCSP = [
    "default-src 'self'",
    isDevelopment
      ? "script-src 'self' 'unsafe-inline' 'unsafe-eval' http://localhost:*" // Dev only — CRA hot reload requires unsafe-inline/eval
      : "script-src 'self'",
    "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com", // unsafe-inline needed for Tailwind/inline styles
    "font-src 'self' https://fonts.gstatic.com",
    "img-src 'self' data: https:",
    isDevelopment
      ? "connect-src 'self' ws://localhost:* http://localhost:*"
      : [
          "connect-src 'self' https://api.nu-data.com wss://api.nu-data.com https://api-rpc.nudata.co.za",
          SSO_ORIGIN,
          ...extra,
        ].join(" "),
    "frame-ancestors 'none'",
    "base-uri 'self'",
    "form-action 'self'",
    "object-src 'none'",
  ];

  return {
    "Content-Security-Policy": baseCSP.join("; "),
    "X-Content-Type-Options": "nosniff",
    "X-Frame-Options": "DENY",
    "X-XSS-Protection": "1; mode=block",
    "Strict-Transport-Security": "max-age=31536000; includeSubDomains; preload",
    "Referrer-Policy": "strict-origin-when-cross-origin",
    "Permissions-Policy": [
      "camera=()",
      "microphone=()",
      "geolocation=()",
      "payment=()",
      "usb=()",
      "magnetometer=()",
      "accelerometer=()",
      "gyroscope=()",
    ].join(", "),
  };
};

export const applySecurityHeaders = (
  isDevelopment: boolean = false,
  extraConnectSrc: readonly string[] = [],
): void => {
  if (typeof document !== "undefined") {
    const headers = getSecurityHeaders(isDevelopment, extraConnectSrc);

    Object.entries(headers).forEach(([name, value]) => {
      const meta = document.createElement("meta");
      meta.httpEquiv = name;
      meta.content = value;
      document.head.appendChild(meta);
    });
  }
};

export const validateCSP = (csp: string): boolean => {
  const requiredDirectives = [
    "default-src",
    "script-src",
    "style-src",
    "img-src",
    "connect-src",
    "frame-ancestors",
  ];

  return requiredDirectives.every((directive) => csp.includes(directive));
};

export const sanitizeInput = (input: string): string => {
  return input
    .replace(/[<>]/g, "")
    .replace(/javascript:/gi, "")
    .replace(/on\w+=/gi, "")
    .trim();
};

export const validateApiUrl = (url: string): boolean => {
  try {
    const parsedUrl = new URL(url);
    const allowedHosts = ["api.nu-data.com", "localhost", "127.0.0.1"];

    return allowedHosts.some(
      (host) =>
        parsedUrl.hostname === host || parsedUrl.hostname.endsWith(`.${host}`),
    );
  } catch {
    return false;
  }
};

export const generateNonce = (): string => {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join(
    "",
  );
};

export const createSecureHeaders = (nonce?: string) => {
  const headers: Record<string, string> = {
    "X-Content-Type-Options": "nosniff",
    "X-Frame-Options": "DENY",
    "X-XSS-Protection": "1; mode=block",
    "Referrer-Policy": "strict-origin-when-cross-origin",
  };

  if (nonce) {
    headers["Content-Security-Policy"] =
      `script-src 'self' 'nonce-${nonce}'; object-src 'none';`;
  }

  return headers;
};
